import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useI18n } from "@/i18n/I18nContext";
import { useQuote } from "@/context/QuoteContext";
import { formatPrice } from "./specIcons";
import { imgUrl } from "@/lib/api";

export default function QuickView({ product, categoryName, onClose }) {
  const { t } = useI18n();
  const { openQuote } = useQuote();
  const navigate = useNavigate();
  if (!product) return null;

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="quick-view-modal" className="max-w-4xl gap-0 overflow-hidden p-0 sm:rounded-none">
        <DialogTitle className="sr-only">{product.name}</DialogTitle>
        <div className="grid grid-cols-1 md:grid-cols-2">
          
          <div className="relative w-full h-fit aspect-[4/3] bg-secondary md:self-center">
            <img src={imgUrl(product.images?.[0])} alt={product.name} className="h-full w-full object-cover" />
            {product.featured && (
              <span className="absolute start-4 top-4 bg-primary px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
                {t("products.featured")}
              </span>
            )}
          </div>

          <div className="flex max-h-[85vh] flex-col overflow-y-auto p-7 md:p-9">
            <span className="text-xs uppercase tracking-[0.3em] text-primary">{categoryName || product.category}</span>
            <h3 className="mt-3 font-display text-2xl font-bold leading-tight tracking-tight md:text-3xl">{product.name}</h3>
            
            {/* 
              REVİZE: description alanına 'whitespace-pre-wrap' sınıfı eklendi.
              Böylece admin panelinde Enter'a basarak geçtiğin alt satırlar korunacak. 
            */}
            {product.description && <p className="mt-4 text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">{product.description}</p>}

            {product.fabric && (
              <p className="mt-5 text-sm"><span className="text-muted-foreground">{t("detail.fabric")}: </span><span className="font-medium">{product.fabric}</span></p>
            )}

            {product.features?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {product.features.map((f) => (
                  <span key={f} className="flex items-center gap-1.5 bg-secondary px-2.5 py-1 text-xs font-medium">
                    <Check className="h-3 w-3 text-primary" /> {f}
                  </span>
                ))}
              </div>
            )}

            {product.sizes?.length > 0 && (
              <div className="mt-5">
                <p className="mb-2 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t("detail.sizes")}</p>
                <div className="flex flex-wrap gap-1.5">
                  {product.sizes.map((s) => (
                    <span key={s} className="min-w-9 border border-border px-2.5 py-1 text-center text-xs font-semibold">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {product.colors?.length > 0 && (
              <div className="mt-5">
                <p className="mb-2 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t("detail.colors")}</p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((c) => (
                    <span key={c.name} className="flex items-center gap-1.5 text-xs">
                      <span className="h-4 w-4 rounded-full border border-border" style={{ background: c.hex }} /> {c.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex items-baseline gap-2">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{t("products.from")}</span>
              <span className="font-display text-2xl font-bold">{formatPrice(product.price)}</span>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                data-testid="quickview-quote-btn"
                onClick={() => { onClose(); openQuote(product); }}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/20 transition-[transform,box-shadow] hover:scale-[1.03] hover:shadow-lg"
              >
                {t("products.getQuote")} <ArrowUpRight className="h-4 w-4" />
              </button>
              <button
                data-testid="quickview-detail-btn"
                onClick={() => { onClose(); navigate(`/urun/${product.id}`); }}
                className="flex flex-1 items-center justify-center gap-2 rounded-full border border-border px-5 py-3 text-sm font-semibold transition-colors hover:bg-secondary"
              >
                {t("products.viewDetail")}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}